import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, X, Star, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";
import { api, mediaUrl, formatApiError } from "../../lib/api";
import ImageField from "./ImageField";
import FocalPointPicker from "./FocalPointPicker";

const inputCls =
  "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-lg px-3 py-2 text-sm outline-none transition-colors";

function SortableRow({ item, testPrefix, reorder, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !reorder,
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={`${testPrefix}-item-${item.id}`}
      style={{ transform: DndCSS.Transform.toString(transform), transition }}
      className={`flex flex-col sm:flex-row sm:items-center gap-4 bg-white border rounded-xl p-4 ${
        isDragging ? "border-[#9A7B66] shadow-xl z-20 relative" : "border-[#E5DED4]"
      }`}
    >
      {reorder && (
        <button
          type="button"
          data-testid={`${testPrefix}-drag-${item.id}`}
          {...attributes}
          {...listeners}
          className="self-start sm:self-center p-2 -ml-1 rounded-md text-[#C9BBA9] hover:text-[#9A7B66] hover:bg-[#F4EFEA] cursor-grab active:cursor-grabbing touch-none transition-colors"
          aria-label="Drag to reorder"
          title="Tahan lalu geser untuk mengubah urutan"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      {children}
    </div>
  );
}

function FieldInput({ field, value, onChange, testId, multiple = false, onChangeMany }) {
  if (field.type === "textarea")
    return <textarea data-testid={testId} rows={field.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />;
  if (field.type === "image") return <ImageField testId={testId} value={value} onChange={onChange} multiple={multiple} onChangeMany={onChangeMany} />;
  if (field.type === "select")
    return (
      <select data-testid={testId} value={value ?? field.options[0]} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  if (field.type === "date")
    return <input data-testid={testId} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  if (field.type === "checkbox")
    return (
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input data-testid={testId} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#9A7B66]" />
        <span className="text-[#6E675F]">{field.hint || "Yes"}</span>
      </label>
    );
  return (
    <input
      data-testid={testId}
      type={field.type === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
      className={inputCls}
    />
  );
}

export default function CollectionTab({
  resource,
  title,
  description,
  fields,
  itemTitle,
  itemSubtitle,
  imageField,
  testPrefix,
  maxItems,
  reorder = false,
  galleryPicker = false,
  multiImageUpload = false,
}) {
  const [items, setItems] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [bulkItems, setBulkItems] = useState([]);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [pickerSelected, setPickerSelected] = useState([]);

  const load = () => api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => {
    setItems(null);
    setEditing(null);
    setBulkItems([]);
    setShowGalleryPicker(false);
    load();
    if (galleryPicker) {
      api.get("/admin/gallery").then((r) => setGalleryItems(r.data)).catch(() => setGalleryItems([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, galleryPicker]);

  const openNew = () => {
    if (maxItems && items?.length >= maxItems) {
      toast.info(`Maximum ${maxItems} items allowed`);
      return;
    }
    const defaults = {};
    fields.forEach((f) => {
      defaults[f.name] = f.type === "checkbox" ? false : f.type === "number" ? 0 : f.type === "select" ? f.options[0] : "";
    });
    setForm(defaults);
    setBulkItems([]);
    setShowGalleryPicker(false);
    setEditing("new");
  };

  const remainingSlots = maxItems ? Math.max(0, maxItems - (items?.length || 0)) : Number.POSITIVE_INFINITY;

  const handleBulkUpload = (urls) => {
    const allowed = Math.max(0, remainingSlots - bulkItems.length);
    const accepted = urls.slice(0, allowed);
    if (urls.length > allowed) toast.error(`Only ${allowed} more photo${allowed === 1 ? "" : "s"} can be added`);
    if (!accepted.length) return;
    setBulkItems((current) => [...current, ...accepted.map((url) => ({ image_url: url, image_focal_point: form.image_focal_point || "50% 0%" }))]);
    setForm((current) => ({ ...current, [imageField]: current[imageField] || accepted[0] }));
  };

  const toggleGalleryChoice = (item) => {
    const metadata = {
      image_url: item.image_url,
      image_alt: item.image_alt || "",
      image_title: item.image_title || "",
      image_description: item.image_description || "",
      image_caption: item.image_caption || item.caption || "",
      image_focal_point: item.image_focal_point || "50% 0%",
    };
    if (editing === "new" && multiImageUpload) {
      const exists = bulkItems.some((bulk) => bulk.image_url === item.image_url);
      if (exists) {
        setBulkItems(bulkItems.filter((bulk) => bulk.image_url !== item.image_url));
        return;
      }
      if (bulkItems.length >= remainingSlots) {
        toast.error(`Maximum ${maxItems} photos allowed`);
        return;
      }
      setBulkItems([...bulkItems, metadata]);
    }
    setForm({ ...form, ...metadata });
  };

  const appendVillaGalleryImages = (galleryItemsToAdd) => {
    const lines = galleryItemsToAdd.map((item) => {
      const parts = [
        item.image_url,
        item.image_alt || "",
        item.image_title || "",
        item.image_description || "",
        item.image_caption || item.caption || "",
        item.image_focal_point || "50% 0%",
      ];
      while (parts.length > 1 && !parts[parts.length - 1]) parts.pop();
      return parts.join(" | ");
    });
    setForm((current) => ({
      ...current,
      gallery_images: [current.gallery_images, ...lines].filter(Boolean).join("\n"),
    }));
  };

  const save = async () => {
    try {
      if (editing === "new" && multiImageUpload && bulkItems.length) {
        const nextOrder = Math.max(0, ...(items || []).map((item) => Number(item.order) || 0)) + 1;
        const payloads = bulkItems.slice(0, remainingSlots);
        for (let index = 0; index < payloads.length; index += 1) {
          await api.post(`/admin/${resource}`, { ...form, ...payloads[index], order: nextOrder + index });
        }
        toast.success(`${payloads.length} photos saved`);
      } else if (editing === "new") await api.post(`/admin/${resource}`, form);
      else await api.put(`/admin/${resource}/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      setBulkItems([]);
      setShowGalleryPicker(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${resource}/${id}`);
    toast.success("Deleted");
    load();
  };

  const quickToggle = async (item, key) => {
    await api.put(`/admin/${resource}/${item.id}`, { [key]: !item[key] });
    load();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const persistOrder = async (ordered) => {
    try {
      await Promise.all(
        ordered.map((item, orderIndex) => api.put(`/admin/${resource}/${item.id}`, { order: orderIndex + 1 }))
      );
      toast.success("Display order updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !items) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    persistOrder(reordered);
  };

  return (
    <div data-testid={`${testPrefix}-tab`}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#2C2825]">{title}</h2>
          {description && <p className="text-sm text-[#A0988E] mt-1">{description}</p>}
          {maxItems && items && (
            <p data-testid={`${testPrefix}-limit-count`} className="text-[11px] uppercase tracking-[0.14em] font-sans font-semibold text-[#9A7B66] mt-2">
              {items.length} / {maxItems} photos
            </p>
          )}
        </div>
        <button
          data-testid={`${testPrefix}-add-button`}
          onClick={openNew}
          disabled={!!maxItems && items?.length >= maxItems}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#9A7B66] hover:bg-[#826552] disabled:opacity-45 disabled:hover:bg-[#9A7B66] text-white text-xs font-semibold uppercase tracking-wider transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" /> Add New
        </button>
      </div>

      {editing !== null && (
        <div data-testid={`${testPrefix}-form`} className="mb-8 bg-white border border-[#E5DED4] rounded-md p-6 space-y-4">
          <h3 className="font-serif text-lg text-[#2C2825]">{editing === "new" ? "Add Item" : "Edit Item"}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => {
              const isBulkImage = multiImageUpload && editing === "new" && f.name === imageField;
              return (
              <div key={f.name} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
                <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A7B66] mb-1.5">{f.label}</label>
                <FieldInput
                  field={f}
                  testId={`form-field-${f.name}`}
                  value={form[f.name]}
                  onChange={(v) => setForm({ ...form, [f.name]: v })}
                  multiple={isBulkImage}
                  onChangeMany={isBulkImage ? handleBulkUpload : undefined}
                />
                {f.type === "image" && (
                  <FocalPointPicker
                    testId={`${testPrefix}-focal-picker`}
                    imageUrl={form[f.name]}
                    value={form.image_focal_point || "50% 0%"}
                    onChange={(v) => setForm({ ...form, image_focal_point: v })}
                  />
                )}
                {isBulkImage && bulkItems.length > 0 && (
                  <div data-testid={`${testPrefix}-bulk-preview`} className="mt-3 rounded-md border border-[#E5DED4] bg-[#FBF9F5] p-3">
                    <p className="text-[10px] uppercase tracking-[0.16em] font-sans font-semibold text-[#A0988E] mb-3">
                      {bulkItems.length} selected photo{bulkItems.length === 1 ? "" : "s"} ready to save
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {bulkItems.map((item, bulkIndex) => (
                        <div key={`${item.image_url}-${bulkIndex}`} className="relative">
                          <img
                            data-testid={`${testPrefix}-bulk-preview-${bulkIndex}`}
                            src={mediaUrl(item.image_url)}
                            alt={item.image_alt || `Selected photo ${bulkIndex + 1}`}
                            className="w-full h-28 rounded-md object-cover border border-[#E5DED4] bg-white"
                          />
                          {item.image_caption && (
                            <span className="absolute bottom-2 right-2 max-w-[85%] rounded-sm bg-[#1E1A17]/75 px-2 py-1 text-[10px] text-white truncate">
                              {item.image_caption}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {resource === "villas" && f.name === "gallery_images" && (
                  <div className="mt-3 rounded-md border border-[#E5DED4] bg-[#FBF9F5] p-3">
                    <ImageField
                      testId="villa-gallery-multi-upload"
                      value=""
                      onChange={() => {}}
                      multiple
                      showInput={false}
                      onChangeMany={(urls) => appendVillaGalleryImages(urls.map((image_url) => ({ image_url })))}
                    />
                    <details className="mt-3">
                      <summary data-testid="villa-gallery-picker-toggle" className="cursor-pointer text-[10px] uppercase tracking-[0.16em] font-sans font-semibold text-[#9A7B66]">
                        Choose from Gallery (SEO fields mirror automatically)
                      </summary>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-56 overflow-y-auto pr-1 mt-3">
                        {galleryItems.map((item, galleryIndex) => {
                          const picked = pickerSelected.some((p) => p.image_url === item.image_url);
                          return (
                            <button
                              key={item.id || galleryIndex}
                              type="button"
                              data-testid={`villa-gallery-choice-${galleryIndex}`}
                              aria-pressed={picked}
                              onClick={() =>
                                setPickerSelected((current) =>
                                  picked ? current.filter((p) => p.image_url !== item.image_url) : [...current, item]
                                )
                              }
                              className={`relative h-20 rounded-md overflow-hidden border-2 transition-colors ${
                                picked ? "border-[#9A7B66]" : "border-transparent hover:border-[#D8CBB8]"
                              }`}
                              title={item.caption || "Gallery image"}
                            >
                              <img src={mediaUrl(item.image_url)} alt={item.image_alt || item.caption || "Gallery"} className="w-full h-full object-cover" />
                              <span
                                className={`absolute top-1.5 right-1.5 rounded-full p-1 transition-colors ${
                                  picked ? "bg-[#9A7B66] text-white" : "bg-white/70 text-transparent border border-[#D8CBB8]"
                                }`}
                              >
                                <Check className="w-3 h-3" />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {pickerSelected.length > 0 && (
                        <button
                          type="button"
                          data-testid="villa-gallery-add-selected"
                          onClick={() => {
                            appendVillaGalleryImages(pickerSelected);
                            setPickerSelected([]);
                            toast.success(`${pickerSelected.length} photo${pickerSelected.length === 1 ? "" : "s"} added`);
                          }}
                          className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-[#9A7B66] hover:bg-[#826552] text-white text-[11px] font-semibold uppercase tracking-wider transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" /> Add {pickerSelected.length} selected photo{pickerSelected.length === 1 ? "" : "s"}
                        </button>
                      )}
                    </details>
                  </div>
                )}
                {galleryPicker && f.name === imageField && (
                  <div className="mt-3">
                    <button
                      type="button"
                      data-testid="hero-gallery-picker-toggle"
                      aria-pressed={showGalleryPicker}
                      onClick={() => setShowGalleryPicker((value) => !value)}
                      className="inline-flex items-center gap-3 rounded-full border border-[#D8CBB8] bg-[#FBF9F5] px-3 py-2 text-[10px] font-sans font-semibold uppercase tracking-[0.14em] text-[#6E675F] hover:border-[#9A7B66] hover:text-[#9A7B66] transition-colors"
                    >
                      <span className={`relative w-9 h-5 rounded-full transition-colors ${showGalleryPicker ? "bg-[#9A7B66]" : "bg-[#D8CBB8]"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showGalleryPicker ? "translate-x-4" : "translate-x-0.5"}`} />
                      </span>
                      {showGalleryPicker ? "Gallery picker on" : "Choose from Gallery"}
                    </button>
                    {showGalleryPicker && (
                      <div data-testid="hero-gallery-picker" className="mt-3 rounded-md border border-[#E5DED4] bg-[#FBF9F5] p-3">
                        <p className="text-[10px] uppercase tracking-[0.16em] font-sans font-semibold text-[#A0988E] mb-3">
                          {multiImageUpload && editing === "new" ? "Select one or more Gallery photos" : "Select one Gallery photo"}
                        </p>
                        {galleryItems.length > 0 ? (
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-48 overflow-y-auto pr-1">
                            {galleryItems.map((item, galleryIndex) => {
                              const selected = bulkItems.some((bulk) => bulk.image_url === item.image_url) || form[f.name] === item.image_url;
                              return (
                                <button
                                  key={item.id || galleryIndex}
                                  type="button"
                                  data-testid={`hero-gallery-choice-${galleryIndex}`}
                                  onClick={() => toggleGalleryChoice(item)}
                                  className={`relative h-20 rounded-md overflow-hidden border-2 transition-colors ${
                                    selected ? "border-[#9A7B66]" : "border-transparent hover:border-[#D8CBB8]"
                                  }`}
                                  title={item.caption || "Gallery image"}
                                >
                                  <img src={mediaUrl(item.image_url)} alt={item.image_alt || item.caption || "Gallery"} className="w-full h-full object-cover" />
                                  <span
                                    className={`absolute top-1.5 right-1.5 rounded-full p-1 transition-colors ${
                                      selected ? "bg-[#9A7B66] text-white" : "bg-white/70 text-transparent border border-[#D8CBB8]"
                                    }`}
                                  >
                                    <Check className="w-3 h-3" />
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-[#A0988E] font-sans">No gallery photos yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              data-testid={`${testPrefix}-save-button`}
              onClick={save}
              className="px-6 py-2.5 rounded-full bg-[#9A7B66] hover:bg-[#826552] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              Save
            </button>
            <button
              data-testid={`${testPrefix}-cancel-button`}
              onClick={() => {
                setEditing(null);
                setShowGalleryPicker(false);
              }}
              className="px-6 py-2.5 rounded-full border border-[#D3C8B9] text-[#6E675F] text-xs font-semibold uppercase tracking-wider hover:bg-[#F4EFEA] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!items ? (
        <p className="text-sm text-[#A0988E]">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#A0988E]">No items yet.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {items.map((item) => (
                <SortableRow key={item.id} item={item} testPrefix={testPrefix} reorder={reorder}>
                  {imageField && item[imageField] && (
                    <img src={mediaUrl(item[imageField])} alt="" className="w-full h-40 sm:w-16 sm:h-16 rounded-lg object-cover shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-sans font-semibold text-sm text-[#2C2825] truncate">
                      {item[itemTitle] || item.guest_name || "Untitled"}
                    </div>
                    {itemSubtitle && item[itemSubtitle] && (
                      <div className="text-xs text-[#A0988E] truncate mt-0.5">{item[itemSubtitle]}</div>
                    )}
                    {resource === "reviews" && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="flex items-center gap-0.5">
                          {Array.from({ length: item.rating || 5 }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-[#9A7B66] text-[#9A7B66]" />
                          ))}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#A0988E]">{item.source}</span>
                        <span
                          className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                            item.approved ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {item.approved ? "Published" : "Pending"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 shrink-0 self-end sm:self-center">
                    {resource === "reviews" && (
                      <button
                        data-testid={`admin-approve-review-button-${item.id}`}
                        onClick={() => quickToggle(item, "approved")}
                        title={item.approved ? "Unpublish" : "Approve & publish"}
                        className={`p-2 rounded-lg transition-colors ${
                          item.approved ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        }`}
                      >
                        {item.approved ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    )}
                    {resource === "hero-slides" && (
                      <button
                        data-testid={`hero-active-toggle-${item.id}`}
                        onClick={() => quickToggle(item, "active")}
                        title={item.active !== false ? "Shown on site" : "Hidden"}
                        className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1.5 rounded-lg ${
                          item.active !== false ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {item.active !== false ? "Live" : "Hidden"}
                      </button>
                    )}
                    {resource === "popups" && (
                      <button
                        data-testid={`popup-active-toggle-${item.id}`}
                        onClick={() => quickToggle(item, "active")}
                        title={item.active !== false ? "Shown in popup rotation" : "Hidden"}
                        className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1.5 rounded-lg ${
                          item.active !== false ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {item.active !== false ? "Live" : "Hidden"}
                      </button>
                    )}
                    <button
                      data-testid={`${testPrefix}-edit-${item.id}`}
                      onClick={() => {
                        setForm({ ...item });
                        setBulkItems([]);
                        setShowGalleryPicker(false);
                        setEditing(item.id);
                      }}
                      className="p-2 rounded-lg bg-[#EFEAE1] text-[#9A7B66] hover:bg-[#9A7B66] hover:text-white transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`${testPrefix}-delete-${item.id}`}
                      onClick={() => remove(item.id)}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </SortableRow>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
